import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

// TODO §BACKEND: GET /api/users/profile/:slug
// const res = await fetch(`${process.env.REACT_APP_API_URL}/users/profile/${slug}`);
// const data = await res.json();

const EXP_LEVEL_OPTIONS = [
  { value: 'Fresher',   label: 'Fresher (0 yrs)' },
  { value: '1-2 years', label: '1-2 years' },
  { value: '3-5 years', label: '3-5 years' },
  { value: '5-8 years', label: '5-8 years' },
  { value: '8+ years',  label: '8+ years' },
];

const AVATAR_COLORS = [
  '2563eb', '16a34a', '7c3aed', 'd97706', 'dc2626', '0891b2', 'be185d',
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

export default function MenteePublicProfile() {
  const { slug } = useParams();
  const isMock = process.env.REACT_APP_MOCK !== 'false';

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const key = `db_mentee_profile_${slug.toLowerCase()}`;

    // 1. Try localStorage — set when the user saves their profile
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        setProfile(JSON.parse(saved));
        setLoading(false);
        return;
      }
    } catch (_) {}

    // 2. Fall back to sessionStorage — logged-in user viewing their own link
    try {
      const raw = sessionStorage.getItem('db_user');
      if (raw) {
        const stored = JSON.parse(raw);
        const emailSlug = (stored.email || '').split('@')[0].toLowerCase();
        if (emailSlug === slug.toLowerCase()) {
          setProfile(stored);
          setLoading(false);
          return;
        }
      }
    } catch (_) {}

    if (isMock) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    // 3. TODO §BACKEND: GET /api/users/profile/:slug
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/users/profile/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setProfile(data.user || data); else setNotFound(true); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line

  const expLabel = EXP_LEVEL_OPTIONS.find(o => o.value === profile?.experienceLevel)?.label || profile?.experienceLevel;
  const avatarColor = getAvatarColor(profile?.name);
  const initial = (profile?.name || 'U').charAt(0).toUpperCase();

  const containerStyle = {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const headerStyle = {
    background: '#fff',
    borderBottom: '1px solid #e5e7eb',
    padding: '14px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: 20,
    border: '1px solid #e5e7eb',
    padding: '32px 36px',
    maxWidth: 680,
    margin: '0 auto',
    boxShadow: '0 4px 24px rgba(0,0,0,.06)',
  };

  if (loading) {
    return (
      <div style={{ ...containerStyle, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ color:'#9ca3af', fontSize:'.95rem' }}>Loading profile...</div>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div style={containerStyle}>
        <header style={headerStyle}>
          <Link to="/" style={{ fontWeight:900, fontSize:'1.1rem', color:'#1a1a2e', textDecoration:'none' }}>
            DevBrains
          </Link>
          <Link to="/mentors" style={{ fontSize:'.84rem', color:'#2563eb', textDecoration:'none', fontWeight:700 }}>
            Find a Mentor →
          </Link>
        </header>
        <div style={{ maxWidth:680, margin:'80px auto', padding:'0 24px', textAlign:'center' }}>
          <div style={{ fontSize:'3rem', marginBottom:16 }}>🔍</div>
          <h2 style={{ fontWeight:900, color:'#1a1a2e', marginBottom:8 }}>Profile Not Found</h2>
          <p style={{ color:'#6b7280', marginBottom:24, lineHeight:1.7 }}>
            The profile <strong>@{slug}</strong> doesn't exist or hasn't been set up yet.
          </p>
          <Link to="/mentors" style={{
            display:'inline-flex', alignItems:'center', gap:6,
            background:'#2563eb', color:'#fff', textDecoration:'none',
            padding:'11px 22px', borderRadius:10, fontWeight:700, fontSize:'.88rem',
          }}>
            Find a Mentor on DevBrains →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Minimal header */}
      <header style={headerStyle}>
        <Link to="/" style={{ fontWeight:900, fontSize:'1.1rem', color:'#1a1a2e', textDecoration:'none' }}>
          DevBrains
        </Link>
        <Link to="/mentors" style={{ fontSize:'.84rem', color:'#2563eb', textDecoration:'none', fontWeight:700 }}>
          Find a Mentor →
        </Link>
      </header>

      {/* Profile content */}
      <div style={{ padding:'40px 24px 80px' }}>
        <div style={cardStyle}>

          {/* Avatar + name row */}
          <div style={{ display:'flex', gap:20, alignItems:'flex-start', marginBottom:24 }}>
            <div style={{
              width:72, height:72, borderRadius:18, flexShrink:0,
              background:`#${avatarColor}`,
              display:'flex', alignItems:'center', justifyContent:'center',
              color:'#fff', fontWeight:900, fontSize:'1.9rem',
            }}>
              {initial}
            </div>

            <div style={{ flex:1, minWidth:0 }}>
              {/* Name + exp badge */}
              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:4 }}>
                <h1 style={{ fontWeight:900, fontSize:'1.2rem', color:'#1a1a2e', margin:0 }}>{profile.name}</h1>
                {expLabel && (
                  <span style={{
                    background:'#eff6ff', color:'#2563eb',
                    fontSize:'.72rem', fontWeight:700,
                    padding:'3px 10px', borderRadius:6,
                  }}>
                    {expLabel}
                  </span>
                )}
                {profile.experienceLevel === 'Fresher' && (
                  <span style={{
                    background:'#f0fdf4', color:'#16a34a',
                    fontSize:'.72rem', fontWeight:700,
                    padding:'3px 10px', borderRadius:6,
                  }}>
                    Fresher
                  </span>
                )}
              </div>

              {/* Headline */}
              {profile.headline && (
                <div style={{ fontSize:'.9rem', color:'#6b7280', marginBottom:6 }}>{profile.headline}</div>
              )}

              {/* Company + Location */}
              <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:'.82rem', color:'#9ca3af' }}>
                {profile.company  && <span>🏢 {profile.company}</span>}
                {profile.location && <span>📍 {profile.location}</span>}
              </div>
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div style={{ marginBottom:20 }}>
              <h2 style={{ fontWeight:800, fontSize:'.88rem', color:'#374151', marginBottom:8, textTransform:'uppercase', letterSpacing:'.04em' }}>About</h2>
              <p style={{ fontSize:'.9rem', color:'#374151', lineHeight:1.75, margin:0 }}>{profile.bio}</p>
            </div>
          )}

          {/* Goals */}
          {profile.goals?.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <h2 style={{ fontWeight:800, fontSize:'.88rem', color:'#374151', marginBottom:10, textTransform:'uppercase', letterSpacing:'.04em' }}>Learning Goals</h2>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {profile.goals.map(g => (
                  <span key={g} style={{
                    background:'#eff6ff', color:'#2563eb',
                    fontSize:'.8rem', fontWeight:700,
                    padding:'5px 14px', borderRadius:8,
                    border:'1px solid #dbeafe',
                  }}>
                    {g}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Social links */}
          {(profile.linkedinUrl || profile.githubUrl || profile.portfolio) && (
            <div style={{ marginBottom:24, display:'flex', gap:12, flexWrap:'wrap' }}>
              {profile.linkedinUrl && (
                <a href={profile.linkedinUrl} target="_blank" rel="noreferrer" style={{
                  display:'inline-flex', alignItems:'center', gap:6,
                  background:'#f0f7ff', color:'#0a66c2',
                  fontSize:'.8rem', fontWeight:700,
                  padding:'6px 14px', borderRadius:8, textDecoration:'none',
                  border:'1px solid #dbeafe',
                }}>
                  in LinkedIn
                </a>
              )}
              {profile.githubUrl && (
                <a href={profile.githubUrl} target="_blank" rel="noreferrer" style={{
                  display:'inline-flex', alignItems:'center', gap:6,
                  background:'#f9fafb', color:'#374151',
                  fontSize:'.8rem', fontWeight:700,
                  padding:'6px 14px', borderRadius:8, textDecoration:'none',
                  border:'1px solid #e5e7eb',
                }}>
                  GitHub
                </a>
              )}
              {profile.portfolio && (
                <a href={profile.portfolio} target="_blank" rel="noreferrer" style={{
                  display:'inline-flex', alignItems:'center', gap:6,
                  background:'#faf5ff', color:'#7c3aed',
                  fontSize:'.8rem', fontWeight:700,
                  padding:'6px 14px', borderRadius:8, textDecoration:'none',
                  border:'1px solid #ede9fe',
                }}>
                  🌐 Portfolio
                </a>
              )}
            </div>
          )}

          {/* Divider */}
          <div style={{ borderTop:'1px solid #f3f4f6', margin:'24px 0' }} />

          {/* CTA */}
          <div style={{ textAlign:'center' }}>
            <p style={{ fontSize:'.84rem', color:'#9ca3af', marginBottom:14 }}>
              Looking for a mentor to guide your tech journey?
            </p>
            <Link to="/mentors" style={{
              display:'inline-flex', alignItems:'center', gap:8,
              background:'#2563eb', color:'#fff', textDecoration:'none',
              padding:'12px 28px', borderRadius:12, fontWeight:800, fontSize:'.9rem',
              boxShadow:'0 2px 8px rgba(37,99,235,.25)',
            }}>
              Find a Mentor on DevBrains →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
